import type { CommandActionContext, CommandDefinition } from '@/data/commands' // Import new types
// Import Command UI components
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { PinIcon, PinOffIcon, SearchIcon } from 'lucide-react'
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/components/ui/sidebar'
// Import store functions
import {
	addRecentCommandId,
	getPinnedCommandIds,
	getRecentCommandIds,
	togglePinnedCommandId,
} from '@/lib/command-palette-store'
import { useEffect, useMemo, useState } from 'react' // Import useMemo

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

// Define the props for NavMain
interface NavMainProps {
	items: CommandDefinition[] // Now expects CommandDefinition[]
	suggestedItems?: CommandDefinition[] // Optional suggested items
	actionContext?: CommandActionContext // Optional context for actions
}

export function NavMain({ items, suggestedItems = [], actionContext }: NavMainProps) {
	const location = useLocation()
	const pathname = location.pathname
	const navigate = useNavigate() // Keep navigate for fallback if actionContext not provided
	const { t } = useTranslation()

	const [open, setOpen] = useState(false)
	// State for pinned and recent IDs
	const [pinnedIds, setPinnedIds] = useState<string[]>([])
	const [recentIds, setRecentIds] = useState<string[]>([])

	// Load initial state from localStorage
	useEffect(() => {
		setPinnedIds(getPinnedCommandIds())
		setRecentIds(getRecentCommandIds())
	}, []) // Empty dependency array ensures this runs only once on mount

	// Effect to handle Cmd+K / Ctrl+K
	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				setOpen((prevOpen) => !prevOpen)
			}
		}

		document.addEventListener('keydown', down)
		return () => document.removeEventListener('keydown', down)
	}, [])

	// Function to handle pinning/unpinning using command.id
	const handleTogglePin = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
		e.stopPropagation()
		togglePinnedCommandId(id)
		setPinnedIds(getPinnedCommandIds())
	}

	// Function to handle command selection (action or navigation)
	const handleSelect = (command: CommandDefinition) => {
		addRecentCommandId(command.id) // Record recent command using id
		setRecentIds(getRecentCommandIds())
		setOpen(false) // Close the dialog immediately

		const context = actionContext ?? { navigate } // Ensure context exists

		// Priority 1: Execute action first if configured and action exists
		if (command.executeActionFirst && command.action) {
			console.log(`Executing action first for: ${command.id}`)
			command.action(context)
			return // Action executed, stop here
		}

		// Priority 2: Navigate if URL exists (and action wasn't prioritized)
		if (command.url) {
			console.log(`Navigating for: ${command.id} to ${command.url}`)
			navigate(command.url)
			return // Navigation executed, stop here
		}

		// Priority 3: Execute action as fallback if no URL but action exists
		if (command.action) {
			console.log(`Executing fallback action for: ${command.id}`)
			command.action(context)
			return // Fallback action executed, stop here
		}

		// If neither action nor URL could be executed
		console.warn(`Command ${command.id} has no valid action or URL to execute.`)
	}

	// Process items into groups using useMemo for efficiency
	const { suggestedGroup, pinnedGroup, recentGroup, otherGroup } = useMemo(() => {
		const suggestedSet = new Set(suggestedItems.map((item) => item.id))
		const pinnedSet = new Set(pinnedIds)
		const recentSet = new Set(recentIds)

		const finalSuggested = suggestedItems // Assume suggestedItems are pre-filtered
		const finalPinned = items.filter((item) => pinnedSet.has(item.id))
		const finalRecent = items.filter(
			(item) =>
				recentSet.has(item.id) &&
				!pinnedSet.has(item.id) && // Exclude if pinned
				!suggestedSet.has(item.id) // Exclude if suggested
		)
		const finalOther = items.filter(
			(item) => !suggestedSet.has(item.id) && !pinnedSet.has(item.id) && !recentSet.has(item.id)
		)

		return {
			suggestedGroup: finalSuggested,
			pinnedGroup: finalPinned,
			recentGroup: finalRecent,
			otherGroup: finalOther,
		}
	}, [items, suggestedItems, pinnedIds, recentIds])

	// Render function for CommandItem using CommandDefinition
	const renderCommandItem = (command: CommandDefinition, isPinned: boolean) => (
		<CommandItem
			key={command.id} // Use command.id as key
			value={`${t(command.nameKey)} ${command.id} ${command.keywords?.join(' ')}`} // Use nameKey, id, keywords for value
			onSelect={() => handleSelect(command)}
			className='group flex items-center justify-between'
		>
			<div className='flex items-center gap-2'>
				{command.icon && <command.icon className='size-4' />}
				<span>{t(command.nameKey)}</span>
			</div>
			{/* Only show pin button for non-suggested items? Or always? Let's show always for now */}
			<Button
				variant='ghost'
				size='icon'
				className='invisible size-6 shrink-0 group-hover:visible'
				onClick={(e) => handleTogglePin(e, command.id)} // Use command.id
				aria-label={isPinned ? t('commands.unpin', 'Unpin command') : t('commands.pin', 'Pin command')}
			>
				{isPinned ? <PinOffIcon className='size-4' /> : <PinIcon className='size-4' />}
			</Button>
		</CommandItem>
	)

	// --- Render Logic ---
	// Extract sidebar rendering logic if it gets too complex
	const sidebarNavItems = items.filter((item) => item.type === 'navigation') // Filter only nav items for sidebar display

	return (
		<>
			{/* Sidebar - Render only navigation items */}
			<SidebarGroup>
				<SidebarGroupContent className='flex flex-col gap-2'>
					<SidebarMenu>
						<SidebarMenuItem>
							<Button
								variant='outline'
								className='flex h-8 w-full items-center justify-start gap-2 px-2 text-sm font-normal text-muted-foreground shadow-none'
								onClick={() => setOpen(true)}
							>
								<SearchIcon className='size-4' />
								<span>{t('sidebar.search.button')}</span>
								<kbd className='pointer-events-none ml-auto hidden h-4 select-none items-center gap-1 rounded border bg-muted px-2 font-mono text-xs font-normal opacity-100 group-data-[collapsible=icon]:hidden sm:flex'>
									<span className='text-xs'>⌘</span>K
								</kbd>
							</Button>
						</SidebarMenuItem>
					</SidebarMenu>
					<SidebarMenu className='flex flex-col gap-1'>
						{/* Map over sidebarNavItems instead of original items */}
						{sidebarNavItems.map((item) => {
							// Need url for Link and isActive check
							if (!item.url) return null
							const isActive = pathname === item.url
							return (
								<SidebarMenuItem key={item.id}>
									{' '}
									{/* Use id as key */}
									<Link to={item.url} className='block w-full'>
										<SidebarMenuButton
											tooltip={t(item.nameKey)}
											data-active={isActive}
											className='w-full data-[active=true]:bg-accent data-[active=true]:text-accent-foreground'
										>
											{item.icon && <item.icon />}
											<span>{t(item.nameKey)}</span>
										</SidebarMenuButton>
									</Link>
								</SidebarMenuItem>
							)
						})}
					</SidebarMenu>
				</SidebarGroupContent>
			</SidebarGroup>
			{/* Updated CommandDialog */}
			<CommandDialog open={open} onOpenChange={setOpen}>
				<CommandInput placeholder={t('sidebar.search.placeholder')} />
				<CommandList>
					<CommandEmpty>{t('sidebar.search.noResults')}</CommandEmpty>

					{/* Suggested Group */}
					{suggestedGroup.length > 0 && (
						<CommandGroup heading={t('sidebar.search.suggestedGroup', 'Suggested')}>
							{suggestedGroup.map((item) => renderCommandItem(item, pinnedIds.includes(item.id)))}
						</CommandGroup>
					)}

					{/* Pinned Group */}
					{pinnedGroup.length > 0 && (
						<>
							{suggestedGroup.length > 0 && <CommandSeparator />}
							<CommandGroup heading={t('sidebar.search.pinnedGroup', 'Pinned')}>
								{/* Pass true for isPinned */}
								{pinnedGroup.map((item) => renderCommandItem(item, true))}
							</CommandGroup>
						</>
					)}

					{/* Recent Group */}
					{recentGroup.length > 0 && (
						<>
							{(suggestedGroup.length > 0 || pinnedGroup.length > 0) && <CommandSeparator />}
							<CommandGroup heading={t('sidebar.search.recentGroup', 'Recent')}>
								{/* Pass false for isPinned, check actual pinned status for icon */}
								{recentGroup.map((item) => renderCommandItem(item, pinnedIds.includes(item.id)))}
							</CommandGroup>
						</>
					)}

					{/* Other Items Group */}
					{otherGroup.length > 0 && (
						<>
							{(suggestedGroup.length > 0 || pinnedGroup.length > 0 || recentGroup.length > 0) && (
								<CommandSeparator />
							)}
							<CommandGroup
								// Use a more generic heading if it includes actions now
								heading={t('sidebar.search.allCommandsGroup', 'All Commands')}
							>
								{/* Pass false for isPinned, check actual pinned status for icon */}
								{otherGroup.map((item) => renderCommandItem(item, pinnedIds.includes(item.id)))}
							</CommandGroup>
						</>
					)}
				</CommandList>
			</CommandDialog>
		</>
	)
}
